import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, CheckCircle2, XCircle, TrendingUp } from 'lucide-react';

export default function FlowDiagram() {
  return (
    <section className="py-10 md:py-16 bg-slate-50">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <motion.h2 
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
          className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#337AF7] tracking-tight text-center mb-12"
          data-testid="flow-title"
        >
          How Trading Compass Works
        </motion.h2>

        <div className="max-w-4xl mx-auto">
          {/* Step 1: Analysis */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            data-testid="flow-node-analysis"
            className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-5">
              {/* Image */}
              <div className="md:col-span-2 relative h-48 md:h-full">
                <img 
                  src="https://images.unsplash.com/photo-1745509267945-b25cbb4d50ef?auto=format&fit=crop&w=800&q=80"
                  alt="Trading analysis"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/40" />
              </div>
              
              {/* Content */}
              <div className="md:col-span-3 p-6 md:p-8 flex items-center">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <BarChart3 className="h-8 w-8 text-[#337AF7]" />
                    <h3 className="text-xl md:text-2xl font-semibold text-[#162959]">
                      1. You Analyse
                    </h3>
                  </div>
                  <p className="text-sm md:text-base text-[#162959]/70">
                    Use your existing strategy and analysis methods. Trading Compass doesn't change what you do here.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Arrow */}
          <div className="flex justify-center mb-6" data-testid="flow-edge-1">
            <div className="h-12 w-px bg-slate-300"></div>
          </div>

          {/* Step 2: Validation */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            data-testid="flow-node-checkpoints"
            className="bg-white rounded-2xl border-2 border-[#337AF7]/30 shadow-sm overflow-hidden mb-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-5">
              {/* Image */}
              <div className="md:col-span-2 relative h-48 md:h-full order-2 md:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                  alt="Trading validation dashboard"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-l md:bg-gradient-to-r from-transparent to-white" />
              </div>
              
              {/* Content */}
              <div className="md:col-span-3 p-6 md:p-8 order-1 md:order-2">
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-full bg-[#337AF7]/10 p-3">
                    <TrendingUp className="h-6 w-6 text-[#337AF7]" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-[#162959]">
                    2. Trading Compass Validates
                  </h3>
                </div>
                <p className="text-sm md:text-base text-[#162959]/70 mb-4">
                  Before you place the trade, the system enforces discipline:
                </p>
                <div className="grid gap-2">
                  <div className="flex items-center gap-3 text-sm md:text-base text-[#162959]/80">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Risk Check: Position size within limits?</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm md:text-base text-[#162959]/80">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>SL/TP Clarity: Stop loss and take profit defined?</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm md:text-base text-[#162959]/80">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>R:R Validation: Risk-reward ratio acceptable?</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm md:text-base text-[#162959]/80">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>Emotional State Check: Are you trading on impulse?</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Arrow */}
          <div className="flex justify-center mb-6" data-testid="flow-edge-2">
            <div className="h-12 w-px bg-slate-300"></div>
          </div>

          {/* Step 3: Execute or Reject */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            data-testid="flow-node-execute"
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div className="bg-white rounded-2xl border-2 border-green-200 p-6 hover:shadow-md transition-shadow duration-200">
              <CheckCircle2 className="h-8 w-8 text-green-500 mb-3" />
              <h4 className="text-lg font-semibold text-[#162959] mb-2">Execute with Confidence</h4>
              <p className="text-sm text-[#162959]/70">
                All checks passed. Trade meets your rules. Execute knowing you followed your process.
              </p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-red-200 p-6 hover:shadow-md transition-shadow duration-200">
              <XCircle className="h-8 w-8 text-red-500 mb-3" />
              <h4 className="text-lg font-semibold text-[#162959] mb-2">Rejected (Save Capital)</h4>
              <p className="text-sm text-[#162959]/70">
                Failed validation. Trade blocked before damage. Capital preserved for better setups.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}